import { Dialog, Transition } from '@headlessui/react'
import { Fragment, PropsWithChildren, ReactNode } from 'react'
import Button from './Button'
import { CloseIcon } from './icons'
import { useTranslation } from 'react-i18next'
import { ErrorBoundary, FallbackProps } from 'react-error-boundary'
import './Modal.css'
import Alert from './Alert'

export interface ModalStateProps {
    open: boolean
    onClose: (open: boolean) => void
}

export interface ModalProps extends ModalStateProps {
    title: ReactNode
    description?: ReactNode
    actions?: ReactNode
    headerActions?: ReactNode
    size?: 'small' | 'regular' | 'large' | 'fullscreen'
    zIndex?: number
}

export default function Modal({
    children,
    description,
    open,
    onClose,
    title,
    actions,
    headerActions,
    size,
    zIndex = 999,
}: PropsWithChildren<ModalProps>) {
    const { t } = useTranslation()

    function fallbackRender({ error }: FallbackProps) {
        return (
            <Alert variant="error" title={t('render_error')}>
                <pre style={{ whiteSpace: 'pre-wrap' }}>{error.message}</pre>
            </Alert>
        )
    }

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog
                as="div"
                className={`modal ${size ?? 'small'}`}
                onClose={onClose}
                style={{ zIndex }}
            >
                <Transition.Child
                    as={Fragment}
                    enter="transition-enter"
                    enterFrom="transition-enter-from"
                    enterTo="transition-enter-to"
                    leave="transition-leave"
                    leaveFrom="transition-leave-from"
                    leaveTo="transition-leave-to"
                >
                    <div className="modal-overlay" style={{ zIndex }} aria-hidden="true" />
                </Transition.Child>
                <div className="modal-wrapper" style={{ zIndex: zIndex + 1 }}>
                    <Transition.Child
                        as={Fragment}
                        enter="transition-enter"
                        enterFrom="transition-enter-from transition-enter-from-scale"
                        enterTo="transition-enter-to"
                        leave="transition-leave"
                        leaveFrom="transition-leave-from"
                        leaveTo="transition-leave-to"
                    >
                        <Dialog.Panel className="modal-inner">
                            <div className="modal-header">
                                {
                                    size === 'fullscreen' && (
                                        <Button
                                            variant="secondary"
                                            onClick={() => onClose(false)}
                                            icon={<CloseIcon />}
                                        >
                                            {t('exit')}
                                        </Button>
                                    )
                                }
                                <Dialog.Title as="h2" className="modal-header-title">{title}</Dialog.Title>
                                {
                                    size === 'fullscreen' && actions && (
                                        <div className="modal-fullscreen-actions">
                                            {actions}
                                        </div>
                                    )
                                }
                                {
                                    headerActions && size !== 'fullscreen' && (
                                        <div className="modal-header-actions">
                                            {headerActions}
                                        </div>
                                    )
                                }
                                {
                                    size !== 'fullscreen' && (
                                        <Button
                                            className="modal-close"
                                            size="small"
                                            variant="plain"
                                            icon={<CloseIcon />}
                                            onClick={() => onClose(false)}
                                        />
                                    )
                                }
                            </div>
                            {
                                description && (
                                    <Dialog.Description className="modal-description">
                                        {description}
                                    </Dialog.Description>
                                )
                            }
                            <ErrorBoundary fallbackRender={fallbackRender}>
                                <div className="modal-content">
                                    {children}
                                </div>
                            </ErrorBoundary>
                            {
                                !!(actions && size !== 'fullscreen') && (
                                    <div className="modal-footer">
                                        {actions}
                                    </div>
                                )
                            }
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    )
}
